import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  MessageCircle, 
  Settings, 
  Sparkles, 
  Trophy,
  Moon,
  Sun,
  LogOut,
  Crown,
  ChevronRight,
  Palette,
  Award,
  Users
} from 'lucide-react';
import { 
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { AuraLevel, getAuraLevel, getLevelProgress } from '@/hooks/useAuraPoints';
import { cn } from '@/lib/utils';

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenMessages: () => void;
  onOpenSettings: () => void;
  onOpenStyleQuiz: () => void;
  onOpenChallenges: () => void;
  onOpenAchievements?: () => void;
  onOpenReferral?: () => void;
  onUpgrade: () => void;
}

export const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
  isOpen,
  onClose,
  onOpenMessages,
  onOpenSettings,
  onOpenStyleQuiz,
  onOpenChallenges,
  onOpenAchievements,
  onOpenReferral,
  onUpgrade,
}) => {
  const { user, profile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const auraPoints = profile?.aura_points ?? 0;
  const auraLevel = (profile?.aura_level as AuraLevel) ?? 'Initiate';
  const levelProgress = getLevelProgress(auraPoints);

  const quickActions = [
    { 
      icon: MessageCircle, 
      label: 'Messages', 
      onClick: () => { onOpenMessages(); onClose(); },
      description: 'Direct messages'
    },
    { 
      icon: Award, 
      label: 'Achievements', 
      onClick: () => { onOpenAchievements?.(); onClose(); },
      description: 'Unlock badges & rewards'
    },
    { 
      icon: Trophy, 
      label: 'Challenges', 
      onClick: () => { onOpenChallenges(); onClose(); },
      description: 'Weekly competitions'
    },
    { 
      icon: Users, 
      label: 'Invite Friends', 
      onClick: () => { onOpenReferral?.(); onClose(); },
      description: 'Earn 100 pts per referral'
    },
    { 
      icon: Palette, 
      label: 'Style Quiz', 
      onClick: () => { onOpenStyleQuiz(); onClose(); },
      description: 'Update your style'
    },
    { 
      icon: Settings, 
      label: 'Settings', 
      onClick: () => { onOpenSettings(); onClose(); },
      description: 'App preferences'
    },
  ];

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="sr-only">Profile Menu</DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-8 space-y-6 overflow-y-auto">
          {/* Profile Card */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-card to-secondary/30 border border-border"
          >
            {/* Avatar */}
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl overflow-hidden bg-muted flex items-center justify-center">
                {profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={28} className="text-muted-foreground" />
                )}
              </div>
              {profile?.is_premium && (
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-aura-gold flex items-center justify-center">
                  <Crown size={10} className="text-background" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg truncate">
                {profile?.display_name || 'AURA Member'}
              </h3>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Sparkles size={12} className="text-aura-gold" />
                <span className="text-xs font-medium text-aura-gold">
                  {auraPoints.toLocaleString()} pts • {auraLevel}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Level Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Level Progress</span>
              <span className="text-foreground font-medium">
                {levelProgress.current} / {levelProgress.next}
              </span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress.percentage}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-aura-gold to-aura-rose rounded-full"
              />
            </div>
            <p className="text-[10px] text-muted-foreground text-center">
              {levelProgress.next - levelProgress.current} pts to {levelProgress.nextLevel}
            </p>
          </div>

          {/* Quick Actions */}
          <div className="space-y-1">
            {quickActions.map((action, idx) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={action.onClick}
                className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-secondary/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                  <action.icon size={18} className="text-muted-foreground" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">{action.label}</p>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </motion.button>
            ))}
          </div>

          {/* Theme Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30">
            <div className="flex items-center gap-3">
              {isDark ? <Moon size={18} /> : <Sun size={18} />}
              <div>
                <p className="text-sm font-medium">Dark Mode</p>
                <p className="text-xs text-muted-foreground">
                  {isDark ? 'On' : 'Off'}
                </p>
              </div>
            </div>
            <Switch checked={isDark} onCheckedChange={toggleTheme} />
          </div>

          {/* AURA+ Upgrade Card */}
          {!profile?.is_premium && (
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => { onUpgrade(); onClose(); }}
              className="w-full p-4 rounded-2xl bg-gradient-to-r from-aura-gold/20 via-aura-rose/10 to-aura-gold/20 border border-aura-gold/30 text-left group"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-aura-gold/20 flex items-center justify-center">
                  <Crown size={20} className="text-aura-gold" />
                </div>
                <div className="flex-1">
                  <p className="font-display text-sm">Upgrade to AURA+</p>
                  <p className="text-xs text-muted-foreground">₹299/month</p>
                </div>
                <ChevronRight size={16} className="text-aura-gold group-hover:translate-x-1 transition-transform" />
              </div>
              <p className="text-xs text-muted-foreground">
                Unlock unlimited AI styling, virtual try-on & exclusive features
              </p>
            </motion.button>
          )}

          {/* Sign Out */}
          <button
            onClick={() => { signOut(); onClose(); }}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut size={16} />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
